import { PanelState } from 'state/PanelState'
import type { Filter } from 'components/filters/useStreamFilters'
import { openPlacementIntentForFilter } from 'lib/threadOpenPlacement'
import { panelSessionPageKey, panelSessionScrollTopForPage } from 'lib/panelSessionScrollScope'

const PANEL_SESSION_STORAGE_PREFIX = 'treechat:panel-session:v1'
export const PANEL_SESSION_OFFSCREEN_BUFFER = 1

type PersistedPanel = {
    panelId?: string
    empty?: boolean
    newThread?: boolean
    stream?: PanelState['stream']
    filter?: Filter
    questId?: string
    query?: string
    savedScrollTop?: number
}

export type PanelSessionSnapshot = {
    version: 1
    updatedAt: number
    pageKey?: string | null
    userId?: string | number | null
    spaceId?: string | number | null
    numPanels: number
    activeIndex: number
    centerIndex: number
    panels: PersistedPanel[]
}

type PanelSessionIdentity = {
    userId?: string | number | null
    spaceId?: string | number | null
}

type BuildInitialPanelsArgs = PanelSessionIdentity & {
    filter: Filter
    numPanels: number
    isMobile: boolean
    fallbackCenterPanelId?: string
}

type ShouldPersistPanel = (panel: PanelState) => boolean

const getPanelSessionStorage = (storageName: 'localStorage' | 'sessionStorage') => {
    if (typeof window === 'undefined') return null

    try {
        return window[storageName] || null
    } catch (_) {
        return null
    }
}

const parseSnapshot = (raw: string | null): PanelSessionSnapshot | null => {
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw) as PanelSessionSnapshot
        if (parsed?.version !== 1 || !Array.isArray(parsed.panels)) return null

        return parsed
    } catch (_) {
        return null
    }
}

const readStorageSnapshot = (storage: Storage | null, key: string): PanelSessionSnapshot | null => {
    if (!storage) return null

    try {
        return parseSnapshot(storage.getItem(key))
    } catch (_) {
        return null
    }
}

const storageKey = ({ userId, spaceId }: PanelSessionIdentity) =>
    [
        PANEL_SESSION_STORAGE_PREFIX,
        userId == null ? 'anonymous' : String(userId),
        spaceId == null ? 'default' : String(spaceId),
    ].join(':')

const getPersistedScrollTop = (panel: PanelState) => {
    try {
        const liveScrollTop = panel.getScrollContainer()?.scrollTop
        if (typeof liveScrollTop === 'number' && Number.isFinite(liveScrollTop)) {
            return liveScrollTop
        }
    } catch (_) {}

    return panel.savedScrollTop
}

const serializePanel = (panel: PanelState): PersistedPanel => {
    if (panel.empty) {
        return {
            panelId: panel.panelId,
            empty: true,
        }
    }

    return {
        panelId: panel.panelId,
        empty: panel.empty,
        newThread: panel.newThread,
        stream: panel.stream,
        filter: panel.filter,
        questId: panel.questId,
        query: panel.query,
        savedScrollTop: getPersistedScrollTop(panel),
    }
}

const deserializePanel = (panel: PersistedPanel) =>
    new PanelState({
        panelId: panel.panelId,
        empty: panel.empty,
        newThread: panel.newThread,
        stream: panel.stream,
        filter: panel.filter,
        questId: panel.questId,
        query: panel.query,
        savedScrollTop: panel.savedScrollTop || 0,
    })

export function sanitizePanelSessionPanels(
    panels: PanelState[],
    shouldPersistPanel: ShouldPersistPanel = () => true,
) {
    return panels.map(panel => {
        if (!panel || panel.empty || shouldPersistPanel(panel)) return panel

        return new PanelState({
            panelId: panel.panelId,
            empty: true,
        })
    })
}

const stableStringify = (value: unknown): string => {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`
    }

    if (value && typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .filter(key => (value as Record<string, unknown>)[key] !== undefined)
            .map(
                key =>
                    `${JSON.stringify(key)}:${stableStringify(
                        (value as Record<string, unknown>)[key],
                    )}`,
            )
            .join(',')}}`
    }

    return JSON.stringify(value)
}

const comparableFilter = (filter?: Filter | null) => {
    if (!filter) return ''
    const { answerId, ...rest } = filter
    return stableStringify(rest)
}

const filtersMatch = (left?: Filter | null, right?: Filter | null) =>
    comparableFilter(left) === comparableFilter(right)

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

const panelWindowAroundCenter = (
    panels: PanelState[],
    centerIndex: number,
    numPanels: number,
    isMobile: boolean,
) => {
    if (!panels.length) return { panels, centerIndex: 0 }

    const onePanel = isMobile || numPanels === 1
    const visibleLeft = onePanel ? 0 : 1
    const visibleRight = onePanel ? 0 : 1
    const start = clamp(
        centerIndex - visibleLeft - PANEL_SESSION_OFFSCREEN_BUFFER,
        0,
        panels.length,
    )
    const end = clamp(
        centerIndex + visibleRight + PANEL_SESSION_OFFSCREEN_BUFFER + 1,
        start,
        panels.length,
    )

    return {
        panels: panels.slice(start, end),
        centerIndex: centerIndex - start,
    }
}

const ensureDesktopBookends = (panels: PanelState[]) => {
    const next = [...panels]
    if (!next[0]?.empty) next.unshift(new PanelState({ empty: true }))
    if (!next[next.length - 1]?.empty) next.push(new PanelState({ empty: true }))
    return next
}

const getCurrentPageKey = () =>
    typeof window === 'undefined' ? null : panelSessionPageKey(window.location)

const scopeSnapshotScroll = (
    snapshot: PanelSessionSnapshot,
    allowScrollRestore: boolean,
): PanelSessionSnapshot => {
    const currentPageKey = getCurrentPageKey()

    return {
        ...snapshot,
        panels: snapshot.panels.map(panel => ({
            ...panel,
            savedScrollTop: panelSessionScrollTopForPage({
                savedScrollTop: panel.savedScrollTop,
                savedPageKey: snapshot.pageKey,
                currentPageKey,
                allowScrollRestore,
            }),
        })),
    }
}

const isHomeRoute = () => {
    if (typeof window === 'undefined') return false

    const pathname = window.location?.pathname || '/'
    return pathname === '/' || pathname === ''
}

export function readPanelSessionSnapshot(
    identity: PanelSessionIdentity,
): PanelSessionSnapshot | null {
    const key = storageKey(identity)
    const localSnapshot = readStorageSnapshot(getPanelSessionStorage('localStorage'), key)
    const sessionSnapshot = readStorageSnapshot(getPanelSessionStorage('sessionStorage'), key)

    // sessionStorage belongs to this browser window, so keep its navigation state ahead of a
    // newer localStorage snapshot written by another window. localStorage remains the shared
    // fallback for restoring the last panel layout, but never contributes a scroll offset.
    if (sessionSnapshot) return scopeSnapshotScroll(sessionSnapshot, true)
    if (localSnapshot) return scopeSnapshotScroll(localSnapshot, false)
    return null
}

function removePanelSessionSnapshot(identity: PanelSessionIdentity) {
    const key = storageKey(identity)
    const localStorage = getPanelSessionStorage('localStorage')
    const sessionStorage = getPanelSessionStorage('sessionStorage')

    try {
        localStorage?.removeItem(key)
    } catch (_) {}

    try {
        sessionStorage?.removeItem(key)
    } catch (_) {}
}

export function writePanelSessionSnapshot(
    identity: PanelSessionIdentity,
    snapshot: Omit<
        PanelSessionSnapshot,
        'version' | 'updatedAt' | 'pageKey' | 'userId' | 'spaceId' | 'panels'
    > & {
        panels: PanelState[]
        isMobile?: boolean
        shouldPersistPanel?: ShouldPersistPanel
    },
) {
    try {
        const panels = sanitizePanelSessionPanels(snapshot.panels, snapshot.shouldPersistPanel)
        if (!panels.some(panel => panel && !panel.empty && panel.filter)) {
            removePanelSessionSnapshot(identity)
            return
        }

        const centerIndex = clamp(snapshot.centerIndex || 0, 0, panels.length - 1)
        const panelWindow = panelWindowAroundCenter(
            panels,
            centerIndex,
            snapshot.numPanels,
            snapshot.isMobile || false,
        )

        const payload: PanelSessionSnapshot = {
            version: 1,
            updatedAt: Date.now(),
            pageKey: getCurrentPageKey(),
            userId: identity.userId,
            spaceId: identity.spaceId,
            numPanels: snapshot.numPanels,
            activeIndex: snapshot.activeIndex || 0,
            centerIndex: panelWindow.centerIndex,
            panels: panelWindow.panels.map(serializePanel),
        }

        const key = storageKey(identity)
        const localStorage = getPanelSessionStorage('localStorage')
        const sessionStorage = getPanelSessionStorage('sessionStorage')
        const sharedPayload: PanelSessionSnapshot = {
            ...payload,
            panels: payload.panels.map(panel => ({ ...panel, savedScrollTop: 0 })),
        }

        if (sessionStorage) {
            try {
                sessionStorage.setItem(key, JSON.stringify(payload))
            } catch (_) {}
        }

        if (localStorage) {
            try {
                localStorage.setItem(key, JSON.stringify(sharedPayload))
            } catch (_) {}
        }
    } catch (_) {}
}

export function buildInitialPanelsFromSession({
    filter,
    numPanels,
    isMobile,
    fallbackCenterPanelId,
    userId,
    spaceId,
}: BuildInitialPanelsArgs): PanelState[] | null {
    const snapshot = readPanelSessionSnapshot({ userId, spaceId })
    if (!snapshot?.panels?.length) return null

    let panels = snapshot.panels.map(panel => {
        // The People stream was removed; retire it from restored sessions.
        if ((panel.filter as Record<string, unknown>)?.people) {
            return deserializePanel({ ...panel, filter: { following: true, order: 'time' } })
        }
        return deserializePanel(panel)
    })
    if (!panels.length) return null

    const historyPanelId =
        typeof window !== 'undefined'
            ? ((window.history?.state?.panelId || null) as string | null)
            : null

    let centerIndex = historyPanelId
        ? panels.findIndex(panel => panel.panelId === historyPanelId)
        : -1

    if (centerIndex < 0) {
        centerIndex = panels.findIndex(panel => !panel.empty && filtersMatch(panel.filter, filter))
    }

    if (centerIndex < 0) {
        centerIndex = clamp(snapshot.centerIndex || 0, 0, panels.length - 1)
    }

    const shouldApplyCurrentFilter = Boolean(historyPanelId) || !isHomeRoute()
    const restoredCenterPanel = panels[centerIndex]
    const centerFilter =
        shouldApplyCurrentFilter || restoredCenterPanel?.empty
            ? filter
            : restoredCenterPanel.filter || filter

    // A deep-linked reload restores the center panel with its old savedScrollTop; the
    // placement intent must own the initial position instead (flag-off: no-op spread).
    const centerPlacementIntent = openPlacementIntentForFilter(centerFilter)
    panels[centerIndex] = new PanelState({
        ...serializePanel(restoredCenterPanel),
        panelId: restoredCenterPanel?.panelId || fallbackCenterPanelId,
        filter: centerFilter,
        empty: false,
        ...(centerPlacementIntent
            ? { pendingOpenPlacement: centerPlacementIntent, savedScrollTop: 0 }
            : {}),
    })

    const panelWindow = panelWindowAroundCenter(panels, centerIndex, numPanels, isMobile)
    panels = panelWindow.panels
    centerIndex = panelWindow.centerIndex

    if (!isMobile && numPanels !== 1) {
        panels = ensureDesktopBookends(panels)
    } else {
        panels = panels.filter(panel => !panel.empty)
        if (!panels.length) {
            panels = [new PanelState({ panelId: fallbackCenterPanelId, filter })]
        }
    }

    return panels
}
